/**
 * Phase 3.1 — Procedure → marker keyword mapper.
 *
 * Pure function. Input: a `ClinicProcedure` row (or compatible
 * `procedure_snapshot`). Output: a marker `kind` string consumed by
 * Odontogram.jsx / ToothSVG.jsx to render the corner badge + tint.
 *
 * The keyword set is the v1 minimum vocabulary (9 markers):
 *   rct       → Root canal treated tooth   ("R")
 *   crown     → Crown / cap                ("C")
 *   bridge    → Bridge pontic or retainer  ("B")
 *   missing   → Missing tooth / extraction ("M")
 *   filled    → Restoration / filling      ("F")
 *   fractured → Fractured / chipped        ("X")
 *   impacted  → Impacted / unerupted       ("I")
 *   implant   → Dental implant             ("Im")
 *   default   → Generic dental procedure   ("F")
 *
 * Lookup is keyword-on-name first (fast, robust to ad-hoc clinic
 * naming) with `category === 'dental'` as the catch-all default.
 *
 * Implant is matched BEFORE impacted so "implant" doesn't fall into the
 * "impact" pattern (impacted regex word-boundary stops it, but ordering
 * here keeps the file readable).
 */

const KEYWORD_RULES = [
  { kind: 'rct', match: /\b(rct|root\s*canal|endo)\b/i },
  { kind: 'crown', match: /\b(crown|cap)\b/i },
  { kind: 'bridge', match: /\bbridge\b/i },
  { kind: 'missing', match: /\b(extract|extraction|missing|removed|tooth\s*loss)\b/i },
  { kind: 'filled', match: /\b(filling|restoration|composite|amalgam|gic)\b/i },
  { kind: 'fractured', match: /\b(fracture|fractured|cracked|chipped)\b/i },
  { kind: 'implant', match: /\b(implant|dental\s*implant|abutment)\b/i },
  { kind: 'impacted', match: /\b(impact|impacted|unerupted)\b/i },
];

// Single-letter / 2-letter glyph rendered on the affected surface for
// each marker kind. Two-letter "Im" disambiguates implant from impacted
// (which keeps "I").
export const MARKER_GLYPH = {
  rct: 'R',
  crown: 'C',
  bridge: 'B',
  missing: 'M',
  filled: 'F',
  fractured: 'X',
  impacted: 'I',
  implant: 'Im',
};

/**
 * @param {{ name?: string, category?: string }} procedure
 * @returns {'rct'|'crown'|'bridge'|'missing'|'filled'|'fractured'|'impacted'|'implant'}
 */
export function markerFor(procedure) {
  const name = procedure?.name || '';
  for (const rule of KEYWORD_RULES) {
    if (rule.match.test(name)) return rule.kind;
  }
  return 'filled';
}

export default markerFor;
